import { useEffect, useState, useRef } from 'react'
import { usePageData } from '@/hooks/usePageData'
import type {
  ExhibitionDetailPageData,
  ExhibitionArtist,
} from '@/types/exhibition'

/*
  Exhibition Details (updated layout per task 26083928 reference).
  Styling lives in src/styles/pages/exhibition-detail.css.
*/

/* ---- Long-form date range formatter: "25 June - 18 July, 2026" ---- */
const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]
function parseDate(d?: string | null): Date | null {
  if (!d) return null
  const dt = new Date(d)
  return isNaN(dt.getTime()) ? null : dt
}
function formatLongDateRange(start?: string | null, end?: string | null): string {
  const s = parseDate(start)
  const e = parseDate(end)
  if (!s && !e) return ''
  if (s && !e) return `${s.getDate()} ${MONTHS[s.getMonth()]}, ${s.getFullYear()}`
  if (!s && e) return `${e!.getDate()} ${MONTHS[e!.getMonth()]}, ${e!.getFullYear()}`
  const sStr = `${s!.getDate()} ${MONTHS[s!.getMonth()]}`
  const eStr = `${e!.getDate()} ${MONTHS[e!.getMonth()]}`
  if (s!.getFullYear() === e!.getFullYear()) {
    return `${sStr} - ${eStr}, ${e!.getFullYear()}`
  }
  return `${sStr}, ${s!.getFullYear()} - ${eStr}, ${e!.getFullYear()}`
}

/* ---- Image Slider ---- */
function ImageSlider({ images: rawImages }: { images: string[] }) {
  const imageUrls = rawImages.filter(Boolean)
  const [currentIndex, setCurrentIndex] = useState(0)
  const touchStartX = useRef(0)

  if (imageUrls.length === 0) {
    return (
      <div className="mig-exhibition-detail-slider mig-exhibition-detail-slider--empty">
        <span className="mig-exhibition-detail-slider-placeholder">Venue shot from inside</span>
      </div>
    )
  }

  const imageUrl = imageUrls[currentIndex] || null

  const goTo = (idx: number) => {
    if (idx < 0) setCurrentIndex(imageUrls.length - 1)
    else if (idx >= imageUrls.length) setCurrentIndex(0)
    else setCurrentIndex(idx)
  }

  return (
    <div className="mig-exhibition-detail-slider">
      <div
        className="mig-exhibition-detail-slider-image"
        onTouchStart={(e) => {
          touchStartX.current = e.touches[0].clientX
        }}
        onTouchEnd={(e) => {
          const diff = touchStartX.current - e.changedTouches[0].clientX
          if (Math.abs(diff) > 50) {
            goTo(diff > 0 ? currentIndex + 1 : currentIndex - 1)
          }
        }}
      >
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={`Exhibition image ${currentIndex + 1}`}
            decoding="async"
          />
        ) : (
          <span className="mig-exhibition-detail-slider-placeholder">Venue shot from inside</span>
        )}
      </div>

      {imageUrls.length > 1 && (
        <>
          <button
            onClick={() => goTo(currentIndex - 1)}
            aria-label="Previous image"
            className="mig-exhibition-detail-slider-nav"
            data-dir="prev"
          >
            &#8249;
          </button>
          <button
            onClick={() => goTo(currentIndex + 1)}
            aria-label="Next image"
            className="mig-exhibition-detail-slider-nav"
            data-dir="next"
          >
            &#8250;
          </button>

          <div className="mig-exhibition-detail-slider-dots">
            {imageUrls.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentIndex(idx)}
                aria-label={`Go to image ${idx + 1}`}
                className="mig-exhibition-detail-slider-dot"
                data-active={idx === currentIndex}
              />
            ))}
          </div>
        </>
      )}
    </div>
  )
}

/* ---- Artist Links Row ---- */
function ArtistLinksGrid({ artists }: { artists: ExhibitionArtist[] }) {
  const sorted = [...artists].sort((a, b) => {
    const nameA = (a.name || '').toUpperCase()
    const nameB = (b.name || '').toUpperCase()
    return nameA < nameB ? -1 : nameA > nameB ? 1 : 0
  })

  return (
    <div className="mig-exhibition-detail-artists-grid">
      {sorted.map((artist) => (
        <a
          key={artist.id}
          href={`/artists/${artist.slug}`}
          className="mig-exhibition-detail-artist-link"
        >
          &gt;{artist.name}
        </a>
      ))}
    </div>
  )
}

/* ======================================================================
   Main Exhibition Detail Page
   ====================================================================== */
export default function ExhibitionDetailPage() {
  const data = usePageData<ExhibitionDetailPageData>()
  const exhibition = data?.exhibition ?? null
  const venue = data?.venue ?? null
  const artists = data?.artists ?? []
  const images = data?.images ?? []

  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    const name = exhibition?.exhibition_name || venue?.venue_name
    if (name) {
      document.title = `${name} | Migration Galleries`
    }
  }, [exhibition?.exhibition_name, venue?.venue_name])

  if (!exhibition) {
    return (
      <section className="mig-exhibition-detail-not-found">
        <h1 className="mig-exhibition-detail-page-title">Exhibition not found</h1>
      </section>
    )
  }

  const heading = exhibition.exhibition_name || venue?.venue_name || 'Exhibition'
  const sortedArtists = [...artists].sort((a, b) =>
    (a.name || '').toUpperCase() < (b.name || '').toUpperCase() ? -1 : 1
  )
  const artistNamesLine = sortedArtists.map((a) => a.name).join(', ')
  const dateRangeLine = formatLongDateRange(exhibition.start_date, exhibition.end_date)

  return (
    <section className="mig-exhibition-detail-section" data-visible={visible}>
      <div className="mig-exhibition-detail-heading">
        <h1 className="mig-exhibition-detail-page-title">{heading}</h1>
        {artistNamesLine && (
          <p className="mig-exhibition-detail-artists-line">{artistNamesLine}</p>
        )}
        {dateRangeLine && (
          <p className="mig-exhibition-detail-date-range">{dateRangeLine}</p>
        )}
      </div>

      <div className="mig-exhibition-detail-content">
        <div className="mig-exhibition-detail-hero">
          <div className="mig-exhibition-detail-hero-text">
            <div
              className="mig-exhibition-detail-description"
              dangerouslySetInnerHTML={{
                __html: exhibition.description || 'No description available.',
              }}
            />

            {(venue?.venue_name || venue?.venue_address) && (
              <div className="mig-exhibition-detail-venue-info">
                {venue?.venue_name && (
                  <p className="mig-exhibition-detail-venue-name">{venue.venue_name}</p>
                )}
                {venue?.venue_address && (
                  <p className="mig-exhibition-detail-venue-address">{venue.venue_address}</p>
                )}
              </div>
            )}
          </div>

          <ImageSlider images={images} />
        </div>

        {artists.length > 0 && (
          <div className="mig-exhibition-detail-artists-section">
            <p className="mig-exhibition-detail-artist-label">ARTIST LINKS:</p>
            <div className="mig-divider-line" />
            <ArtistLinksGrid artists={artists} />
          </div>
        )}

        <div className="mig-page-back-row">
          <a href="/exhibitions" className="mig-exhibition-detail-back-button">BACK</a>
          <div className="mig-divider-line" />
        </div>
      </div>
    </section>
  )
}
